import { useQuery } from "@tanstack/react-query";
import { PackageOpen, Pencil, Plus, Settings2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { api, jsonBody, queryString } from "../../api/client";
import type { InventoryCategory, InventoryItem, ListResponse } from "../../api/types";
import { useVenueScope } from "../../app/venueScope";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows } from "../../auth/permissions";
import { formatMoney, formatQuantity } from "../../lib/format";
import {
  Card,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Modal,
  PageHeader,
} from "../../ui/components";
import { InventorySkeleton } from "../../ui/skeletons";
import type { StockLevel } from "./inventoryTypes";

/**
 * The simple per-venue inventory list: every item the selected venue stocks, with its
 * category, quantity on hand, and price per item.
 *
 * The advanced machinery behind it (lots, serialized assets, kits, blind counts) still
 * exists in the API for history and integrations, but this screen no longer sells it.
 * Quantities change through the movements endpoint so every change carries an audited
 * reason, and the price is the item's replacement value so nothing new is stored.
 */

type ItemModal = { mode: "create" } | { mode: "edit"; item: InventoryItem } | null;

export function InventoryPage() {
  const auth = useAuth();
  const canManage = membershipAllows(auth.session?.selectedMembership, "inventory.manage");
  const { selectedVenueId, loading: venueLoading } = useVenueScope();
  const [itemModal, setItemModal] = useState<ItemModal>(null);
  const [quantityTarget, setQuantityTarget] = useState<InventoryItem | null>(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [error, setError] = useState<unknown>();
  const [working, setWorking] = useState(false);

  const items = useQuery({
    queryKey: ["inventory", "items", selectedVenueId],
    queryFn: () =>
      api<ListResponse<InventoryItem>>(
        `/api/v1/inventory/items${queryString({ venueId: selectedVenueId })}`,
      ),
    // The venue scope resolves asynchronously; asking with an empty venue would answer
    // with every venue for a moment and then snap to one, which reads as a glitch.
    enabled: Boolean(selectedVenueId),
  });
  const stock = useQuery({
    queryKey: ["inventory", "stock", selectedVenueId],
    queryFn: () =>
      api<ListResponse<StockLevel>>(
        `/api/v1/inventory/stock${queryString({ venueId: selectedVenueId })}`,
      ),
    enabled: Boolean(selectedVenueId),
  });
  const categories = useQuery({
    queryKey: ["inventory", "categories"],
    queryFn: () => api<ListResponse<InventoryCategory>>("/api/v1/inventory/categories"),
  });

  if (
    venueLoading ||
    categories.isPending ||
    (Boolean(selectedVenueId) && (items.isPending || stock.isPending))
  ) {
    return <InventorySkeleton />;
  }
  if (!selectedVenueId) {
    return (
      <>
        <PageHeader
          title="Inventory"
          description="The items this venue stocks, with quantity on hand and price per item."
        />
        <EmptyState
          title="No venue available"
          description="Create a venue to start tracking its inventory."
        />
      </>
    );
  }
  const queryError = items.error ?? stock.error ?? categories.error;
  if (queryError) return <ErrorPanel error={queryError} />;

  const itemList = items.data?.items ?? [];
  const stockList = stock.data?.items ?? [];
  const categoryList = categories.data?.items ?? [];
  const editing = itemModal?.mode === "edit" ? itemModal.item : null;

  function quantityMilliFor(itemId: string): number {
    return stockList
      .filter((level) => level.itemId === itemId)
      .reduce((sum, level) => sum + level.onHandMilli, 0);
  }

  // Quantity edits land on the item's unassigned stock row, so the version handed to the
  // movements endpoint must belong to that row alone; 0 lets the server open a fresh one.
  function baseVersionFor(itemId: string): number {
    return stockList.find((level) => level.itemId === itemId && !level.locationId)?.version ?? 0;
  }

  async function run(action: () => Promise<unknown>, finish: () => void) {
    setWorking(true);
    setError(undefined);
    try {
      await action();
      finish();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function saveItem(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!itemModal) return;
    const current = itemModal.mode === "edit" ? itemModal.item : null;
    const data = new FormData(event.currentTarget);
    const price = String(data.get("price"));
    const payload = {
      // A new item always belongs to the venue in the top bar; an edit keeps whatever
      // venue the item already had, including the shared no-venue case.
      venueId: current ? (current.venueId ?? null) : selectedVenueId,
      sku: String(data.get("sku")),
      name: String(data.get("name")),
      category: String(data.get("category")),
      unit: current?.unit ?? "each",
      tracking: current?.tracking ?? "quantity",
      reorderLevelMilli: current?.reorderLevelMilli ?? 0,
      replacementValueMinor: price ? Math.round(Number(price) * 100) : null,
      active: current?.active ?? true,
    };
    await run(
      async () => {
        await (current
          ? api(`/api/v1/inventory/items/${current.id}`, {
              method: "PUT",
              body: jsonBody(payload),
            })
          : api("/api/v1/inventory/items", { method: "POST", body: jsonBody(payload) }));
        await items.refetch();
      },
      () => setItemModal(null),
    );
  }

  async function saveQuantity(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const target = quantityTarget;
    if (!target) return;
    const data = new FormData(event.currentTarget);
    const nextMilli = Math.round(Number(data.get("quantity")) * 1000);
    const deltaMilli = nextMilli - quantityMilliFor(target.id);
    if (deltaMilli === 0) {
      setQuantityTarget(null);
      return;
    }
    await run(
      async () => {
        await api("/api/v1/inventory/movements", {
          method: "POST",
          body: jsonBody({
            itemId: target.id,
            locationId: null,
            bookingId: null,
            type: "adjust",
            quantityMilli: deltaMilli,
            reason: String(data.get("reason")),
            reference: "",
            version: baseVersionFor(target.id),
          }),
        });
        await stock.refetch();
      },
      () => setQuantityTarget(null),
    );
  }

  async function addCategory(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await run(
      async () => {
        await api("/api/v1/inventory/categories", {
          method: "POST",
          body: jsonBody({ name: String(data.get("name")) }),
        });
        await categories.refetch();
      },
      () => form.reset(),
    );
  }

  async function renameCategory(event: FormEvent<HTMLFormElement>, category: InventoryCategory) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const name = String(data.get("name"));
    if (name === category.name) return;
    await run(
      async () => {
        await api(`/api/v1/inventory/categories/${category.id}`, {
          method: "PUT",
          body: jsonBody({ name, sortOrder: category.sortOrder }),
        });
        await categories.refetch();
      },
      () => undefined,
    );
  }

  async function removeCategory(category: InventoryCategory) {
    await run(
      async () => {
        await api(`/api/v1/inventory/categories/${category.id}`, { method: "DELETE" });
        await categories.refetch();
      },
      () => undefined,
    );
  }

  const addItemButton = canManage ? (
    <button
      className="button primary"
      type="button"
      onClick={() => {
        setError(undefined);
        setItemModal({ mode: "create" });
      }}
    >
      <Plus size={17} /> Add item
    </button>
  ) : undefined;

  return (
    <>
      <PageHeader
        title="Inventory"
        description="The items this venue stocks, with quantity on hand and price per item."
        actions={
          canManage ? (
            <div className="inline">
              <button
                className="button secondary"
                type="button"
                onClick={() => {
                  setError(undefined);
                  setCategoriesOpen(true);
                }}
              >
                <Settings2 size={17} /> Manage categories
              </button>
              {addItemButton}
            </div>
          ) : undefined
        }
      />
      <Card>
        {itemList.length ? (
          <DataTable
            caption="Inventory items"
            items={itemList}
            getKey={(item) => item.id}
            columns={[
              {
                label: "Item",
                render: (item) => (
                  <>
                    <strong>{item.name}</strong>
                    <br />
                    <small>{item.sku}</small>
                  </>
                ),
              },
              { label: "Category", render: (item) => item.category },
              {
                label: "Quantity",
                className: "numeric",
                render: (item) => formatQuantity(quantityMilliFor(item.id)),
              },
              {
                label: "Price per item",
                className: "numeric",
                render: (item) =>
                  item.replacementValueMinor == null
                    ? "Not set"
                    : formatMoney(item.replacementValueMinor),
              },
              {
                label: "",
                render: (item) => (
                  <div className="inline">
                    <button
                      className="button secondary small"
                      type="button"
                      onClick={() => {
                        setError(undefined);
                        setQuantityTarget(item);
                      }}
                    >
                      <PackageOpen size={15} /> Quantity
                    </button>
                    {canManage ? (
                      <button
                        className="button ghost small"
                        type="button"
                        onClick={() => {
                          setError(undefined);
                          setItemModal({ mode: "edit", item });
                        }}
                      >
                        <Pencil size={15} /> Edit
                      </button>
                    ) : null}
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No items yet"
            description="Add the first item to start tracking what this venue stocks."
            action={addItemButton}
          />
        )}
      </Card>

      <Modal
        open={itemModal !== null}
        onClose={() => setItemModal(null)}
        title={editing ? `Edit ${editing.name}` : "Add item"}
        description={
          editing
            ? "Rename the item, move it to another category, or update its price."
            : "The item joins this venue's list; set its quantity from the list afterwards."
        }
      >
        {error ? <ErrorPanel error={error} /> : null}
        {itemModal ? (
          <form className="form-stack" onSubmit={saveItem}>
            <Field label="Name">
              <input
                data-autofocus
                name="name"
                required
                maxLength={200}
                defaultValue={editing?.name ?? ""}
              />
            </Field>
            <Field label="Category">
              <select
                name="category"
                required
                defaultValue={editing?.category ?? categoryList[0]?.name ?? ""}
              >
                {editing && !categoryList.some((entry) => entry.name === editing.category) ? (
                  <option value={editing.category}>{editing.category}</option>
                ) : null}
                {categoryList.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Price per item (USD)">
              <input
                name="price"
                type="number"
                min={0}
                step={0.01}
                defaultValue={
                  editing?.replacementValueMinor != null
                    ? (editing.replacementValueMinor / 100).toFixed(2)
                    : ""
                }
              />
            </Field>
            <Field label="SKU" hint="A short unique code used on labels and exports.">
              <input name="sku" required maxLength={80} defaultValue={editing?.sku ?? ""} />
            </Field>
            <FormActions
              submitting={working}
              submitLabel={editing ? "Save item" : "Add item"}
              onCancel={() => setItemModal(null)}
            />
          </form>
        ) : null}
      </Modal>

      <Modal
        open={quantityTarget !== null}
        onClose={() => setQuantityTarget(null)}
        title={quantityTarget ? `Quantity of ${quantityTarget.name}` : "Update quantity"}
        description="The difference is recorded as an audited stock adjustment with your reason."
      >
        {error ? <ErrorPanel error={error} /> : null}
        {quantityTarget ? (
          <form className="form-stack" onSubmit={saveQuantity}>
            <Field label="Quantity on hand">
              <input
                data-autofocus
                name="quantity"
                type="number"
                min={0}
                step={0.001}
                required
                defaultValue={quantityMilliFor(quantityTarget.id) / 1000}
              />
            </Field>
            <Field label="Reason" hint="Kept in the audit history alongside the movement.">
              <textarea name="reason" required minLength={3} maxLength={1000} />
            </Field>
            <FormActions
              submitting={working}
              submitLabel="Save quantity"
              onCancel={() => setQuantityTarget(null)}
            />
          </form>
        ) : null}
      </Modal>

      <Modal
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
        title="Manage categories"
        description="Every venue shares this list. Items keep their label when a category is renamed or deleted."
      >
        {error ? <ErrorPanel error={error} /> : null}
        <div className="stack">
          <form className="split" onSubmit={addCategory}>
            <input
              data-autofocus
              name="name"
              required
              maxLength={100}
              placeholder="New category name"
              aria-label="New category name"
            />
            <button className="button primary small" type="submit" disabled={working}>
              <Plus size={15} /> Add
            </button>
          </form>
          {categoryList.map((category) => (
            <form
              className="split"
              key={category.id}
              onSubmit={(event) => renameCategory(event, category)}
            >
              <input
                name="name"
                required
                maxLength={100}
                defaultValue={category.name}
                aria-label={`Rename ${category.name}`}
              />
              <div className="inline">
                <button className="button secondary small" type="submit" disabled={working}>
                  Rename
                </button>
                <button
                  className="button danger small"
                  type="button"
                  disabled={working}
                  onClick={() => removeCategory(category)}
                >
                  Delete
                </button>
              </div>
            </form>
          ))}
        </div>
      </Modal>
    </>
  );
}
